import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../cx.js';
import { Button } from './Button.js';

export type { ColumnDef } from '@tanstack/react-table';

export interface PaginazioneTabella {
  pagina: number;
  perPagina: number;
  totale: number;
  onPagina: (pagina: number) => void;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  /** Chiave stabile di riga. */
  getRowId: (row: T) => string;
  /** Mostrato quando non ci sono righe. */
  empty: ReactNode;
  /** Paginazione lato server. */
  pagination?: PaginazioneTabella;
  loading?: boolean;
  caption: string;
  className?: string;
  testi?: { precedente: string; successiva: string; riepilogo: (da: number, a: number, totale: number) => string };
}

const testiDefault = {
  precedente: 'Pagina precedente',
  successiva: 'Pagina successiva',
  riepilogo: (da: number, a: number, totale: number) => `${da}–${a} di ${totale}`,
};

/** Tabella dell'area amministratore: body-small, intestazioni in label, paginazione. */
export function DataTable<T>({ columns, data, getRowId, empty, pagination, loading, caption, className, testi = testiDefault }: DataTableProps<T>) {
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel(), getRowId });
  const ultimaPagina = pagination ? Math.max(1, Math.ceil(pagination.totale / pagination.perPagina)) : 1;

  if (!loading && data.length === 0) return <div className={className}>{empty}</div>;

  return (
    <div className={cx('flex flex-col gap-4', className)} aria-busy={loading || undefined}>
      <div className="overflow-x-auto rounded-lg border border-border bg-surface">
        <table className="w-full t-body-small">
          <caption className="sr-only">{caption}</caption>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-border">
                {hg.headers.map((h) => (
                  <th key={h.id} scope="col" className="t-label text-left px-4 py-3 whitespace-nowrap">
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-b-0">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totale > pagination.perPagina && (
        <nav aria-label="Paginazione" className="flex items-center justify-between gap-4">
          <span className="t-body-small text-ink-muted">
            {testi.riepilogo(
              (pagination.pagina - 1) * pagination.perPagina + 1,
              Math.min(pagination.pagina * pagination.perPagina, pagination.totale),
              pagination.totale,
            )}
          </span>
          <div className="flex gap-2">
            <Button icon={ChevronLeft} disabled={pagination.pagina <= 1} onClick={() => pagination.onPagina(pagination.pagina - 1)} aria-label={testi.precedente}>
              {testi.precedente}
            </Button>
            <Button icon={ChevronRight} disabled={pagination.pagina >= ultimaPagina} onClick={() => pagination.onPagina(pagination.pagina + 1)} aria-label={testi.successiva}>
              {testi.successiva}
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}
